/**
 * TaggedInSection — the stuff you're @mentioned in, on the full Plate view.
 *
 * Mentions live in the `mentions/` collection (NOT `todos`), so without this
 * section the full Plate said "Clean plate" while the dashboard widget showed
 * a pile of tags. Rows match the widget's "Tagged in" language: blue AtSign
 * tile + dot for unread, muted for read, per-row Done (archives the mention),
 * click navigates to the source entity. Collapsible; renders nothing when
 * there are no active mentions (the parent gates on that too).
 *
 * Two piles: unread on top, and anything already read cascaded down into a
 * collapsed "Earlier" group so a tag you've been and looked at stops taking
 * up space without being deleted. Clicking a row reads it.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AtSign, Check, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { buildMentionNavPath } from '@/services/mention.service';
import { MentionStatus, type Mention } from '@/types/mention.types';
import { getLocaleSync } from '@/utils/locale';

function relTime(iso?: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(getLocaleSync(), { day: 'numeric', month: 'short' });
}

interface TaggedInSectionProps {
  mentions: Mention[];
  onClear: (mentionId: string) => void;
  onNavigate: (path: string) => void;
  /** Cascade — reads every tag on the entity the row points at. */
  onOpen?: (mention: Mention) => void;
  /** Sweep the read pile. Omit to hide the control. */
  onClearRead?: () => void;
}

export function TaggedInSection({
  mentions,
  onClear,
  onNavigate,
  onOpen,
  onClearRead,
}: TaggedInSectionProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);

  if (mentions.length === 0) return null;

  const unread = mentions.filter((m) => m.status === MentionStatus.UNREAD);
  const earlier = mentions.filter((m) => m.status !== MentionStatus.UNREAD);

  return (
    <div className="rounded-2xl border border-theme bg-surface-card overflow-hidden">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 group"
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronRight className="w-4 h-4 text-theme-muted" />
        ) : (
          <ChevronDown className="w-4 h-4 text-theme-muted" />
        )}
        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Tagged in</span>
        {/* Count is UNREAD, not total — a read tag isn't something you owe. */}
        <span className="text-[10px] font-semibold text-theme-muted/70">{unread.length}</span>
        <span className="ml-auto text-[10px] text-theme-muted/60 group-hover:text-theme-muted transition-colors">
          {collapsed ? 'Show' : 'Hide'}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-1.5 pb-1.5 space-y-0.5">
              <AnimatePresence initial={false}>
                {unread.map((mention) => (
                  <TaggedRow
                    key={mention.id}
                    mention={mention}
                    onClear={onClear}
                    onNavigate={onNavigate}
                    onOpen={onOpen}
                  />
                ))}
              </AnimatePresence>

              {unread.length === 0 && earlier.length > 0 && (
                <p className="px-2.5 py-2 text-[11px] text-theme-muted">
                  Nothing new — everything you've read is under Earlier.
                </p>
              )}

              {earlier.length > 0 && (
                <div className="pt-1">
                  <div className="flex items-center gap-1 px-2.5">
                    <button
                      type="button"
                      onClick={() => setShowEarlier((v) => !v)}
                      className="flex items-center gap-1.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-theme-muted hover:text-theme-secondary transition-colors"
                      aria-expanded={showEarlier}
                    >
                      {showEarlier ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                      Earlier
                      <span className="text-theme-muted/70">{earlier.length}</span>
                    </button>
                    {onClearRead && (
                      <button
                        type="button"
                        onClick={onClearRead}
                        className="ml-auto py-1 text-[10px] text-theme-muted hover:text-theme-secondary transition-colors"
                        title="Clear everything you've already read"
                      >
                        Clear read
                      </button>
                    )}
                  </div>

                  {showEarlier && (
                    <div className="space-y-0.5 mt-0.5">
                      <AnimatePresence initial={false}>
                        {earlier.map((mention) => (
                          <TaggedRow
                            key={mention.id}
                            mention={mention}
                            onClear={onClear}
                            onNavigate={onNavigate}
                            onOpen={onOpen}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TaggedRow({
  mention,
  onClear,
  onNavigate,
  onOpen,
}: {
  mention: Mention;
  onClear: (id: string) => void;
  onNavigate: (path: string) => void;
  onOpen?: (mention: Mention) => void;
}) {
  const unread = mention.status === MentionStatus.UNREAD;
  const hasExcerpt = !!mention.noteExcerpt;
  const path = buildMentionNavPath(mention);

  const title = hasExcerpt ? `“${mention.noteExcerpt}”` : `${mention.createdByName} mentioned you`;
  const meta = [
    hasExcerpt ? mention.createdByName : undefined,
    mention.entityTitle,
    relTime(mention.createdAt),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 24, height: 0, marginBottom: 0 }}
      transition={{ duration: 0.18 }}
      className="group flex items-center gap-1 rounded-xl overflow-hidden pr-1.5 hover:bg-surface-hover transition-colors"
    >
      <button
        type="button"
        onClick={() => {
          // Opening it reads it — and every other tag on the same entity.
          onOpen?.(mention);
          if (path) onNavigate(path);
        }}
        disabled={!path && !onOpen}
        className="flex-1 min-w-0 flex items-center gap-2.5 text-left px-2.5 py-2 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-secondary/40 disabled:cursor-default"
      >
        <span
          className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
            unread ? 'bg-blue-500/15 text-blue-400' : 'bg-surface-raised text-theme-muted'
          }`}
        >
          <AtSign className="w-3.5 h-3.5" strokeWidth={2.2} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5">
            {unread && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" aria-label="Unread" />}
            <span className="text-[13px] font-medium text-theme-primary truncate leading-snug">{title}</span>
          </span>
          {meta && <span className="block text-[11px] text-theme-muted truncate mt-0.5">{meta}</span>}
        </span>
        {path && (
          <ArrowRight className="w-3.5 h-3.5 text-transparent group-hover:text-theme-muted/60 shrink-0 transition-colors" />
        )}
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (mention.id) onClear(mention.id);
        }}
        aria-label="Done"
        title="Done"
        className="-m-1 p-2.5 shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
      >
        <span className="w-[18px] h-[18px] rounded-full border border-theme hover:border-emerald-400 hover:bg-emerald-500/10 text-transparent hover:text-emerald-400 flex items-center justify-center transition-colors">
          <Check className="w-3 h-3" strokeWidth={3} />
        </span>
      </button>
    </motion.div>
  );
}
